import { httpClient } from "../http-client";
import type {
  ApiResponseProductsEntityList,
  ApiResponseProductLightDtoList
} from "../../../shared/types/api";
import type {
  ShopProductListResponse,
  ShopProductDetailResponse,
  ProductOptionApiResponse,
  ProductOptionRequest,
  CalculatePriceApiResponse,
} from "../../../domain/types/shop-product";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";

export class ShopProductApiService {
  async getProducts(shopId: number): Promise<ShopProductListResponse> {
    const response = await httpClient.get<ShopProductListResponse>(
      `/api/shop/${shopId}/product`
    );

    if (!response.data) {
      throw new Error("상품 목록을 불러오지 못했습니다.");
    }

    // API 응답 형식: { data: [...], errorMsg: string | null, errorCode: string | null }
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 목록을 불러오지 못했습니다.");
    }

    return response.data;
  }

  async getProductDetail(shopId: number, productId: number): Promise<ShopProductDetailResponse> {
    const response = await httpClient.get<ShopProductDetailResponse>(
      `/api/shop/${shopId}/product/${productId}`
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 정보를 불러오지 못했습니다.");
    }

    return response.data;
  }
  
  async getBestsellerList(
    shopId: number,
  ): Promise<ApiResponseProductLightDtoList> {
    const response = await httpClient.get<ApiResponseProductLightDtoList>(
      NEW_API_ENDPOINTS.shopProductController.GET.bestsellerList(shopId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "카테고리를 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  async getRecentlyViewedProducts(
    shopId: number,
  ): Promise<ApiResponseProductsEntityList> {
    const response = await httpClient.get<ApiResponseProductsEntityList>(
      NEW_API_ENDPOINTS.shopController.GET.recentlyViewedProducts(shopId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "최근 둘러본 상품을 가져오지 못했습니다.",
      );
    }

    return response.data;
  }
  
  async getProductOptions(
    shopId: number,
    productId: number,
    request?: ProductOptionRequest
  ): Promise<ProductOptionApiResponse> {
    const response = await httpClient.post<ProductOptionApiResponse>(
      `/api/shop/${shopId}/product/${productId}/option`,
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 정보를 불러오지 못했습니다.");
    }

    return response.data;
  }

  async getProductList(
    shopId: number,
  ): Promise<ApiResponseProductLightDtoList> {
    const response = await httpClient.get<ApiResponseProductLightDtoList>(
      NEW_API_ENDPOINTS.shopProductController.GET.productList(shopId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "최근 둘러본 상품을 가져오지 못했습니다.",
      );
    }

    return response.data;
  }
      
  async calculatePrice(
    shopId: number,
    productId: number,
    request: ProductOptionRequest
  ): Promise<CalculatePriceApiResponse> {
    const response = await httpClient.post<CalculatePriceApiResponse>(
      `/api/shop/${shopId}/product/${productId}/calculate`,
      request
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "가격 계산에 실패했습니다.");
    }

    return response.data;
  }
}

export const shopProductApiService = new ShopProductApiService();
